import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { useSeo } from "@/hooks/useSeo";
import { articles, categories } from "@/mocks/content";

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") ?? "all";
  const [keyword, setKeyword] = useState(searchParams.get("q") ?? "");

  const allTags = useMemo(() => {
    const set = new Set<string>();
    articles.forEach((a) => a.tags.forEach((t) => set.add(t)));
    return Array.from(set).slice(0, 10);
  }, []);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return articles
      .filter((a) => (activeCategory === "all" ? true : a.categorySlug === activeCategory))
      .filter((a) => {
        if (!kw) return true;
        return (
          a.title.toLowerCase().includes(kw) ||
          a.summary.toLowerCase().includes(kw) ||
          a.tags.some((t) => t.toLowerCase().includes(kw))
        );
      })
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [activeCategory, keyword]);

  const setCategory = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    if (slug === "all") next.delete("category");
    else next.set("category", slug);
    setSearchParams(next);
  };

  const activeName =
    activeCategory === "all"
      ? "全部话题"
      : categories.find((c) => c.slug === activeCategory)?.name ?? "全部话题";

  useSeo({
    title: "话题库 - 黑料吃话全部深扒解读合集",
    description:
      "黑料吃话话题库汇集全部娱乐热点深扒解读，支持按栏目筛选与关键词搜索，涵盖剪辑争议、联名风波、番位规则、选角传闻与舆论观察等话题。",
    keywords: "黑料吃话,话题库,黑料吃话解读,热点深扒,幕后分析,舆论观察",
    path: "/topics",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "话题库 - 黑料吃话",
        description: "按栏目浏览与搜索黑料吃话的全部深度解读文章",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${window.location.origin}/` },
          { "@type": "ListItem", position: 2, name: "话题库", item: `${window.location.origin}/topics` },
        ],
      },
    ],
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "话题库" }]} />

      <main className="flex-1">
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="mx-auto w-full px-4 md:px-8">
            <span className="text-xs font-medium tracking-[0.24em] text-primary-600">TOPIC LIBRARY</span>
            <h1 className="mt-2 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">话题库</h1>
            <p className="mt-4 max-w-2xl text-sm leading-8 text-foreground-600">
              这里收录了「黑料吃话」的全部深度解读。按栏目筛选，或用关键词直接搜索你关心的热点类型，找到对应的时间线与分析框架。
            </p>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="relative w-full sm:max-w-md">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索标题、摘要或标签，例如「剪辑」"
                  aria-label="搜索解读文章"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>
              <span className="text-sm text-foreground-500">
                共找到 <strong className="text-primary-600">{filtered.length}</strong> 篇解读
              </span>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="mx-auto w-full px-4 md:px-8">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
              <div className="lg:col-span-8">
                <div className="mb-6 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setCategory("all")}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                      activeCategory === "all"
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-200 text-foreground-700 hover:bg-background-300"
                    }`}
                  >
                    全部
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.slug}
                      type="button"
                      onClick={() => setCategory(cat.slug)}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                        activeCategory === cat.slug
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-200 text-foreground-700 hover:bg-background-300"
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>

                {filtered.length > 0 ? (
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {filtered.map((article) => (
                      <ArticleCard key={article.slug} article={article} />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-20 text-center">
                    <i className="ri-inbox-2-line text-3xl text-foreground-400"></i>
                    <p className="mt-4 font-heading text-base font-semibold text-foreground-800">
                      没有找到匹配的解读
                    </p>
                    <p className="mt-2 text-sm text-foreground-500">换个关键词，或选择「全部」栏目再看看。</p>
                    <button
                      type="button"
                      onClick={() => {
                        setKeyword("");
                        setCategory("all");
                      }}
                      className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      重置筛选条件
                    </button>
                  </div>
                )}
              </div>

              <aside className="lg:col-span-4">
                <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                  <h2 className="font-heading text-lg font-bold text-foreground-950">栏目导航</h2>
                  <ul className="mt-4 space-y-2">
                    <li>
                      <button
                        type="button"
                        onClick={() => setCategory("all")}
                        className={`flex w-full cursor-pointer items-center justify-between rounded-md px-3 py-2.5 text-sm transition-colors ${
                          activeCategory === "all"
                            ? "bg-primary-100 text-primary-700"
                            : "text-foreground-700 hover:bg-background-200"
                        }`}
                      >
                        <span>全部话题</span>
                        <span className="text-xs text-foreground-500">{articles.length}</span>
                      </button>
                    </li>
                    {categories.map((cat) => {
                      const count = articles.filter((a) => a.categorySlug === cat.slug).length;
                      return (
                        <li key={cat.slug}>
                          <button
                            type="button"
                            onClick={() => setCategory(cat.slug)}
                            className={`flex w-full cursor-pointer items-center justify-between rounded-md px-3 py-2.5 text-sm transition-colors ${
                              activeCategory === cat.slug
                                ? "bg-primary-100 text-primary-700"
                                : "text-foreground-700 hover:bg-background-200"
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              <i className={`${cat.icon} text-secondary-600`}></i>
                              {cat.name}
                            </span>
                            <span className="text-xs text-foreground-500">{count}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-6">
                  <h2 className="font-heading text-lg font-bold text-foreground-950">热门标签</h2>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {allTags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => setKeyword(tag)}
                        className="cursor-pointer whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
                      >
                        #{tag}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-6 rounded-lg border border-primary-200 bg-primary-50 p-6">
                  <i className="ri-quill-pen-line text-2xl text-primary-600"></i>
                  <h2 className="mt-3 font-heading text-base font-bold text-foreground-950">想让我们写点什么？</h2>
                  <p className="mt-2 text-sm leading-7 text-foreground-600">
                    把你看到的热点和疑惑发过来，我们会挑选值得深挖的选题做解读。
                  </p>
                  <Link
                    to="/contact"
                    className="mt-4 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-send-plane-line"></i>提交选题
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}